import Layout from '../../components/layout'
import { Title, breakpoints } from '../../styles/shared.css'
import styled from 'styled-components'
import { getPostBySlug, getAllPosts, markdownToHtml } from '../../lib/blog'

export async function getStaticProps({ params }) {
  const data = getPostBySlug(params.slug)
  const content = await markdownToHtml(data.content || '')
  const post = { ...data, content }

  return {
    props: { post },
  }
}

export async function getStaticPaths() {
  const posts = getAllPosts(['slug'])

  return {
    paths: posts.map((post) => {
      return {
        params: {
          slug: post.slug,
        },
      }
    }),
    fallback: false,
  }
}

const Hero = styled.div`
  margin: 40px 0 0;

  h1 {
    margin-bottom: 15px;
  }

  @media (max-width: ${breakpoints.small}) {
    margin: 20px 0 0;
  }
`

const AllPostsLink = styled.a`
  color: #4888e8;
  font-weight: bold;
  text-transform: uppercase;

  &:hover span {
    text-decoration: underline;
  }
`

const PostTitle = styled(Title)`
  margin: 20px 0 0;
  font-size: 38px;
  line-height: 46px;
  font-weight: bold;
`

const PublishDate = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #888;
`

const Byline = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #888;

  &:hover a span.name {
    text-decoration: underline;
  }
`

const AuthorImage = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #222;
  display: inline-block;
  vertical-align: middle;
  margin: 0 0 0 12px;
`

const PostBody = styled.div`
  margin: 50px 0;
  font-size: 16px;
  line-height: 28px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";

  h2 {
    font-weight: 500;
    font-size: 22px;
    margin: 30px 0 8px;

    strong {
      font-weight: 700;
    }
  }

  p {
    margin: 8px 0 16px;

    strong {
      font-weight: 700;
    }
  }

  code {
    font-family: "SFMono-Regular", Menlo, Consolas, "PT Mono", "Liberation Mono", Courier, monospace;
    background: #ddd; 
    border-radius: 3px;
    padding: 2px 5px;
    display: inline-block;
    max-width: 100%;
  }

  ol {
    list-style-type: decimal;
    padding: 0 0 0 20px;
    li {
    }
  }

  img {
    width: 100%;
    border: 1px solid #222;
  }

  blockquote {
    border-left: 5px solid #aaa;
    padding-left: 12px;

    p {
      font-family: "SFMono-Regular", Menlo, Consolas, "PT Mono", "Liberation Mono", Courier, monospace;
    }
  }

  a {
    text-decoration: underline;
    color: #4888e8;
  }

  em {
    font-style: italic;
  }
`

export default function Post({ post }) {
  const authorName = post.author.name
  const authorURL = authorName === 'Ryan'
    ? 'https://goddamnyouryan.com'
    : 'https://piinecone.com'

  const date = new Date(post.date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return <>
    <Hero>
      <AllPostsLink href="/blog">&larr; <span>all posts</span></AllPostsLink>
      <PostTitle>{post.title}</PostTitle>
      <PublishDate>{date}</PublishDate>
      <Byline>&nbsp;&#183; by <a href={authorURL} rel="noreferrer" target="_blank"><span className="name">{authorName}</span> <span>&#x2197;</span></a></Byline>
      <a href={authorURL} rel="noreferrer" target="_blank"><AuthorImage src={post.author.picture} /></a>
    </Hero>
    <PostBody dangerouslySetInnerHTML={{ __html: post.content }} />
    <Ad />
  </>
}

const AdBox = styled.a`
  display: block;
  cursor: pointer;
  background: #fdfdfd;
  padding: 30px 40px;
  margin: 0 0 100px;
  border-radius: 2px;
  border: 1px solid #aaa;

  .pause {
    display: block;
  }

  .animate {
    display: none;
  }

  &:hover {
    background: #fff;
    border: 1px solid #4888e8;

    .pause {
      display: none;
    }

    .animate {
      display: block;
    }
  }

  @media (max-width: ${breakpoints.small}) {
    .animate {
      display: block;
    }

    .pause {
      display: none;
    }
  }

  h1 {
    font-size: 22px;
    font-weight: 700;
    color: #222;
    margin: 0 0 18px;
    line-height: 30px;
  }

  h2 {
    font-size: 16px;
    margin: 8px 0 0;
    color: #555;
    line-height: 22px;

    span {
      display: inline-block;
      padding: 9px;
      border-radius: 4px;
      text-align: center;
      width: 115px;
      margin-right: 4px;
      text-transform: uppercase;
      font-size: 12px;
      letter-spacing: 1px;
      vertical-align: baseline;

      &.blue {
        background: #4888e8;
        color: #fff;
      }

      &.green {
        background: #39DCBF;
        color: #fff;
      }

      @media (max-width: ${breakpoints.small}) {
        background: none;
        padding: 0;
        width: auto;
        margin: 0;
        font-weight: 600;
        font-size: 14px;
        letter-spacing: 0.2px;

        &.blue {
          color: #4888e8;
          background: none;
        }

        &.green {
          color: #39DCBF;
          background: none;
        }
      }
    }
  }
`

const GifContainer = styled.div`
  background: #000;
  border-bottom: 1px solid black;
`

const GifWrap = styled.div`
  margin: 24px auto 0;
  width: 100%;
  overflow: hidden;

  @media (max-width: ${breakpoints.small}) {
    max-width: 100%;
    border-right: 1px solid black;
  }
`

const PausedGif = styled.img`
  border: 1px solid #000;
  width: 100%;
  heith: calc(auto - 2px)
  background: black;
  transform: translate(1px, 0);
`

const Video = styled.video`
  border: 1px solid #000;
  width: 100%;
  background: black;
`

const Ad = () => {
  return <AdBox href="/">
    <h1>Polyfill matches developers and employers together</h1>
    <h2><span className="blue">Developers</span> find jobs they actually want.</h2>
    <h2><span className="green">Employers</span> reach developers who want to hear from them.</h2>
    <GifContainer>
      <GifWrap>
        <PausedGif loading="lazy" className="pause" src="/blog/polyfill-blog-ad-paused.webp" />
        <Video className="animate" playsInline autoPlay muted loop>
          <source src="/blog/polyfill-blog-ad.webm" type="video/webm"></source>
          <source src="/blog/polyfill-blog-ad.mp4" type="video/mp4"></source>
        </Video>
      </GifWrap>
    </GifContainer>
  </AdBox>
}

Post.getLayout = function getLayout(page) {
  const { post } = page.props

  const meta = {
    title: post.title,
    url: `https://polyfill.work/blog/${post.slug}`,
    description: post.excerpt,
    image: post.ogImage.url,
    relativeImageURL: post.ogImage.relativeURL,
  }

  return (
    <Layout meta={meta}>
      {page}
    </Layout>
  )
}